import { useCallback, useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { refreshViaHandle } from './external/refreshViaHandle';
import {
  registerTokenRefresher,
  type RefreshOutcome,
} from './tokenRefresher';

import { useAuthStore } from '@/store/authStore';

// Auth0 error codes that mean the session is genuinely gone (terminal), vs
// transient/network failures which must NOT log the user out.
const AUTH0_TERMINAL_ERRORS = new Set([
  'login_required',
  'consent_required',
  'missing_refresh_token',
]);

export function useRegisterTokenRefresher(): void {
  const { getAccessTokenSilently } = useAuth0();
  const isExternalAuth = useAuthStore((state) => state.isExternalAuth);

  // Stable identities are load-bearing: registerTokenRefresher only preserves an
  // in-flight refresh when the registered fn keeps the same identity across
  // re-renders (otherwise single-flight breaks).
  const externalRefresher = useCallback(
    (): Promise<RefreshOutcome> => refreshViaHandle(),
    [],
  );

  const auth0Refresher = useCallback(async (): Promise<RefreshOutcome> => {
    try {
      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      return token
        ? { status: 'success', accessToken: token }
        : { status: 'transient' };
    } catch (e) {
      const code = (e as { error?: string } | null)?.error;
      if (code && AUTH0_TERMINAL_ERRORS.has(code)) {
        return { status: 'terminal', reason: 'grant_revoked' };
      }
      return { status: 'transient' };
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    registerTokenRefresher(isExternalAuth ? externalRefresher : auth0Refresher);
    return () => registerTokenRefresher(null);
  }, [isExternalAuth, externalRefresher, auth0Refresher]);
}
